import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronDownIcon, ChevronRightIcon } from '@treams/treams-icons';
import { Checkbox, Label, Paragraph, tokens } from '@treams/treams-ui';

import { PermissionNode, buildTree, selectedCount, toggleNode } from './permissions-tree';

const Chevron = styled(Box)<{ $hidden?: boolean }>`
	width: 20px;
	height: 20px;
	cursor: pointer;
	visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};

	svg {
		display: block;
		width: 20px;
		height: 20px;

		path {
			fill: ${tokens.text.secondary};
		}
	}
`;

/** Width of the chevron, so each level lines up under its parent's label. */
const INDENT_PX = 24;

const Row = styled(Flex)`
	cursor: pointer;
	border-radius: 4px;

	&:hover {
		background: ${(props) => props.theme.colors.lightBlueLight};
	}
`;

/**
 * Three-layer whitelist editor (group > type > capability). `permissions` is
 * the single source of truth: every checkbox derives from it and a click only
 * reports the next list through `onChange`. The only local state is which
 * nodes are expanded.
 * @param props - props
 * @param props.permissions - The API user's current whitelist (source of truth)
 * @param props.onChange - Receives the full next whitelist after a click
 * @param props.disabled - Renders read-only checkboxes
 * @returns The tree
 */
export default function PermissionsTree(props: {
	permissions: string[];
	onChange: (next: string[]) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation();
	const tree = useMemo(() => buildTree(), []);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const toggleExpanded = (key: string) =>
		setExpanded((current) => {
			const next = new Set(current);
			if (!next.delete(key)) {
				next.add(key);
			}
			return next;
		});

	const renderNode = (node: PermissionNode, depth: number) => {
		const count = selectedCount(node, props.permissions);
		const partial = count > 0 && count < node.leaves.length;
		const isOpen = expanded.has(node.key);
		const label =
			depth === 0 ? t(`settings.connectors.graphqlApi.groups.${node.label}`) : node.label;
		const toggleChecked = () =>
			!props.disabled && props.onChange(toggleNode(node, props.permissions));

		return (
			<Box key={node.key}>
				<Row alignItems="center" pl={`${depth * INDENT_PX}px`} py="2px">
					<Chevron $hidden={!node.children} onClick={() => toggleExpanded(node.key)}>
						{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
					</Chevron>
					<Checkbox checked={count > 0} disabled={props.disabled} onClick={toggleChecked} />
					<Label
						flexGrow={1}
						onClick={() => (node.children ? toggleExpanded(node.key) : toggleChecked())}
					>
						{label}
					</Label>
					{partial && (
						<Paragraph small color="grey" pr={2}>
							{count}/{node.leaves.length}
						</Paragraph>
					)}
				</Row>
				{isOpen && node.children?.map((child) => renderNode(child, depth + 1))}
			</Box>
		);
	};

	return <Box>{tree.map((node) => renderNode(node, 0))}</Box>;
}
